import React, { useEffect, useLayoutEffect, useState } from 'react'
import dayjs from 'dayjs'
import { css } from '@emotion/core'
import 'dayjs/locale/ja'
import { fetchDiscount, fetchSales } from '@/apis/aipass'
import { UsageDetails } from '@/components/organisms/customer/sales-manager/_sales/usage-details'
import { Discount } from '@/components/organisms/customer/sales-manager/_sales/discount'
import { Adjustment } from '@/components/organisms/customer/sales-manager/_sales/adjustment'
import { LoadingFull } from '@/components/molecules/loading-full'
import { EditDiscountType, EditSalesType, PaymentSettingType, SalesDiscountType, SalesType } from '@/models/sales'
import { ReservationType } from '@/models/reservation'
import { reduceTaxAmount } from '@/libs/calculator/tax'
import { ReceiptPopup } from '@/components/organisms/reservation/receipt-popup'
import { SplitReceiptPopup } from '@/components/organisms/reservation/receipt-split-popup'
import { saleDetailContainerStyle } from '@/constants/layout'
import { useReservationDetailSalesMasters } from '@/hooks/use-reservation-detail-sales-masters'
import { calcSalesPaymentTotals, SalesPaymentTotalType } from '@/utils/sales-payment-total'

type SalesProps = {
  checkinId: string
  customer: { id: string; guest: { name: string; company?: string } }
  customerLink?: any
  // 値が変わるたびに売上を取り直す
  reloadKey: number
  reservations: ReservationType[]
  currentTabReservationId: string
  onUpdateSales: () => void | Promise<void>
}

export const Sales: React.FC<SalesProps> = ({
  checkinId,
  customer,
  customerLink,
  reloadKey,
  reservations,
  currentTabReservationId,
  onUpdateSales,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { masters, isMastersLoaded } = useReservationDetailSalesMasters()
  const {
    salesSubjects,
    salesDiscounts,
    accountsReceivables: accountsReceivableSettings,
    advanceCompany,
    salesSubjectSets,
    salesSubjectTaxes,
  } = masters
  const [usageDetails, setUsageDetails] = useState<SalesType[]>([])
  const [editUsageDetails, setEditUsageDetails] = useState<EditSalesType[]>([])
  const [originUsageDetails, setOriginUsageDetails] = useState<SalesType[]>([])
  const [discounts, setDiscounts] = useState<SalesDiscountType[]>([])
  const [editDiscounts, setEditDiscounts] = useState<EditDiscountType[]>([])
  const [originDiscounts, setOriginDiscounts] = useState<SalesDiscountType[]>([])
  const [confirmedDay, setConfirmedDay] = useState<string>('')
  const [useCheckinDay, setUserCheckinDay] = useState<string>('')
  const [salesPayments, setSalesPayments] = useState<PaymentSettingType[]>([])
  const [salesDiscountPayments, setSalesDiscountPayments] = useState<PaymentSettingType[]>([])
  const [totalSalesPrice, setTotalSalesPrice] = useState<number>(0)
  const [totalTax, setTotalTax] = useState<number>(0)
  const [totalSalesPaymentPrice, setTotalSalesPaymentPrice] = useState<number>(0)
  const [totalUnpaid, setTotalUnpaid] = useState<number>(0)
  const [totalSalesPayments, setTotalSalesPayments] = useState<SalesPaymentTotalType[]>([])
  const [totalSalesDiscountPrice, setTotalSalesDiscountPrice] = useState<number>(0)
  const [salesAdvanceCompanyIds, setSalesAdvanceCompanyIds] = useState<any[]>([])
  const [salesDiscountPaymentsGroupBySalesDate, setSalesDiscountPaymentsGroupBySalesDate] = useState<any[]>([])
  const [filterGuestRoomAssignId, setFilterGuestRoomAssignId] = useState<string[] | undefined>()
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState<boolean>(false)
  const [isSplitReceiptModalOpen, setIsSplitReceiptModalOpen] = useState<boolean>(false)

  const syncUsageDetailsFromOrigin = () => {
    const cloneUsageDetails = originUsageDetails.map(v => ({ ...v }))
    if (filterGuestRoomAssignId) {
      setUsageDetails(cloneUsageDetails.filter(sale => filterGuestRoomAssignId.includes(sale.guestRoomAssignId || '')))
    } else {
      setUsageDetails(cloneUsageDetails)
    }
  }

  const syncDiscountsFromOrigin = () => {
    const cloneDiscounts = originDiscounts.map(v => ({ ...v }))
    if (filterGuestRoomAssignId) {
      setDiscounts(cloneDiscounts.filter(sale => filterGuestRoomAssignId.includes(sale.guestRoomAssignId || '')))
    } else {
      setDiscounts(cloneDiscounts)
    }
  }

  const _fetchSales = async () => {
    await fetchSales(checkinId).then(res => {
      setOriginUsageDetails(res?.sales ?? [])
      setConfirmedDay(res?.confirmedDay)
      setUserCheckinDay(dayjs(res?.userCheckinDay).format('YYYY-MM-DD'))
      const companyIds = res?.sales
        .filter(item => item.companyId)
        .map(item => item.companyId)
        .reduce((unique, item) => (unique.includes(item) ? unique : [...unique, item]), [])
      setSalesAdvanceCompanyIds(companyIds)
      setSalesDiscountPaymentsGroupBySalesDate(res?.salesGroupBySalesDate)
    })
  }
  const _fetchDiscount = async () => {
    await fetchDiscount(checkinId).then(res => {
      setOriginDiscounts(res?.sales ?? [])
    })
  }

  const refreshSalesData = async () => {
    await Promise.all([_fetchSales(), _fetchDiscount()])
  }

  const handleUpdateSale = async () => {
    await refreshSalesData()
    await onUpdateSales()
  }

  // 割引の支払方法は支払方法の一覧を起点に下の useLayoutEffect で絞り込むため、両方を同じタイミングで入れる
  useEffect(() => {
    if (!isMastersLoaded) return
    setSalesPayments(masters.salesPayments)
    setSalesDiscountPayments(masters.salesPayments)
  }, [isMastersLoaded, masters])

  // When a statement's payment method is updated, update the eligible payment
  useLayoutEffect(() => {
    const unsettledPayment = salesDiscountPayments[0]
    if (unsettledPayment == null) {
      return
    }
    const newSalesDiscountPayments: PaymentSettingType[] = [unsettledPayment]
    salesPayments.filter(salesPayment => {
      totalSalesPayments.filter(totalSalesPayment => {
        if (totalSalesPayment.id === salesPayment.id) {
          newSalesDiscountPayments.push(salesPayment)
        }
      })
    })
    const deleteDuplication = newSalesDiscountPayments.filter(
      (element, index, self) => element != null && self.findIndex(e => e?.id === element.id) === index,
    )
    setSalesDiscountPayments(deleteDuplication)
    const discountCompanyIds = totalSalesPayments.filter(v => v.companyId).map(v => v.companyId)
    setSalesAdvanceCompanyIds(
      [...(salesAdvanceCompanyIds || []), ...(discountCompanyIds || [])].filter((value, index, self) => self.indexOf(value) === index),
    )
  }, [totalSalesPayments, salesPayments])

  useEffect(() => {
    if (checkinId) {
      _fetchSales()
      _fetchDiscount()
    }
  }, [checkinId, reloadKey])

  // calculate
  useEffect(() => {
    if (!usageDetails) {
      return
    }
    // total sales
    const totalPrice = usageDetails?.reduce((p, usageDetail) => p + Number(usageDetail.salesPrice), 0)
    setTotalSalesPrice(totalPrice)
    // consumption tax
    setTotalTax(reduceTaxAmount(usageDetails))
    const salesPaymentTotals = calcSalesPaymentTotals(usageDetails, discounts, salesPayments)
    setTotalSalesDiscountPrice(salesPaymentTotals.totalDiscount)
    setTotalSalesPayments(salesPaymentTotals.totalSalesPayments)
    setTotalSalesPaymentPrice(salesPaymentTotals.totalSalesPaymentPrice)
    // accounts receivable
    setTotalUnpaid(usageDetails.reduce((p, usageDetail) => p + Number(usageDetail.unpaid), 0))
    // Accounts Receivable minus Discount Accounts Receivable = Total Sales - Total Receipts
    setTotalUnpaid(totalPrice - salesPaymentTotals.totalSalesPaymentPrice)
  }, [discounts, usageDetails, salesPayments])

  useEffect(() => {
    syncUsageDetailsFromOrigin()
  }, [filterGuestRoomAssignId, originUsageDetails])

  useEffect(() => {
    syncDiscountsFromOrigin()
  }, [filterGuestRoomAssignId, originDiscounts])

  return (
    <>
      <div css={saleDetailContainerStyle}>
        <UsageDetails
          advanceCompany={advanceCompany}
          checkinId={checkinId}
          salesPayments={salesPayments}
          salesSubjects={salesSubjects}
          salesSubjectSets={salesSubjectSets}
          salesSubjectTaxes={salesSubjectTaxes}
          totalUnpaid={totalUnpaid}
          setIsLoading={setIsLoading}
          originUsageDetails={originUsageDetails}
          usageDetails={usageDetails}
          setUsageDetails={setUsageDetails}
          editUsageDetails={editUsageDetails}
          setEditUsageDetails={setEditUsageDetails}
          confirmedDay={confirmedDay}
          useCheckinDay={useCheckinDay}
          reservations={reservations}
          accountsReceivableSettings={accountsReceivableSettings}
          currentTabReservationId={currentTabReservationId}
          onUpdateUsageDetails={handleUpdateSale}
          onRefreshSalesData={refreshSalesData}
          receiptProps={{
            customerId: customer.id,
            customerName: customer.guest.name,
            customerLink,
            setIsReceiptModalOpen,
            setIsSplitReceiptModalOpen,
          }}
          filterGuestRoomAssignId={filterGuestRoomAssignId}
          setFilterGuestRoomAssignId={setFilterGuestRoomAssignId}
        />
        <div css={sectionBottomStyle}>
          <Discount
            advanceCompany={advanceCompany}
            checkinId={checkinId}
            customerId={customer.id}
            customerName={customer.guest.name}
            salesDiscounts={salesDiscounts}
            salesDiscountPayments={salesDiscountPayments}
            setIsLoading={setIsLoading}
            discounts={discounts}
            originDiscounts={originDiscounts}
            setDiscounts={setDiscounts}
            editDiscounts={editDiscounts}
            setEditDiscounts={setEditDiscounts}
            confirmedDay={confirmedDay}
            useCheckinDay={useCheckinDay}
            accountsReceivableSettings={accountsReceivableSettings}
            reservations={reservations}
            currentTabReservationId={currentTabReservationId}
            salesDiscountPaymentsGroupBySalesDate={salesDiscountPaymentsGroupBySalesDate}
            onUpdateDiscounts={handleUpdateSale}
            onRefreshSalesData={refreshSalesData}
          />
          <Adjustment
            advanceCompany={advanceCompany}
            salesPayments={salesPayments}
            accountsReceivableSettings={accountsReceivableSettings}
            isMastersLoaded={isMastersLoaded}
            totalSalesPrice={totalSalesPrice}
            totalTax={totalTax}
            totalSalesPaymentPrice={totalSalesPaymentPrice}
            totalSalesDiscountPrice={totalSalesDiscountPrice}
            totalUnpaid={totalUnpaid}
            onAdjustSales={handleUpdateSale}
            totalSalesPayments={totalSalesPayments}
            currentTabReservationId={currentTabReservationId}
            checkinId={checkinId}
            setIsLoading={setIsLoading}
            onSendPos={handleUpdateSale}
            filterGuestRoomAssignId={filterGuestRoomAssignId}
            usageDetails={usageDetails}
            discounts={discounts}
          />
        </div>
        <LoadingFull isLoading={isLoading} />
      </div>
      {isReceiptModalOpen && (
        <ReceiptPopup
          onClose={() => setIsReceiptModalOpen(false)}
          customer={customer}
          checkinId={checkinId}
          filterGuestRoomAssignId={filterGuestRoomAssignId}
          totalAmount={totalUnpaid}
        />
      )}
      {isSplitReceiptModalOpen && (
        <SplitReceiptPopup
          usageDetails={usageDetails}
          discounts={discounts}
          onClose={() => setIsSplitReceiptModalOpen(false)}
          checkinId={checkinId}
          customer={customer}
        />
      )}
    </>
  )
}

const sectionBottomStyle = css({
  margin: '0 32px',
  display: 'flex',
  justifyContent: 'space-between',
  '@media(max-width: 1080px)': { marginInline: 16 },
})
